
import React, {useState, useEffect, useContext, useRef} from "react";
import {useHistory} from "react-router-dom";
import classNames from "classnames";
import {
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Row,
  Col,
  Button,
} from "reactstrap";

import {
    Tooltip,
    OverlayTrigger
} from "react-bootstrap";

import ReactTable from "components/ReactTable/ReactTable.js";
import SweetAlert from "react-bootstrap-sweetalert";
import NotificationAlert from "react-notification-alert";
import rest from "../../API/api";
import authService from "../../services/authService";
import { UserLoginContext } from "../../context/UserLoginContext";

const UsersTable = () => {
    const [userState, setUserState] = useContext(UserLoginContext);
    const [users, setUsers] = React.useState([]);
    const [selectedUser, setSelectedUser] = React.useState(null);
    const [alert, setalert] = React.useState(null);
    const history = useHistory();
    const [dataLoaded, setDataLoaded] = useState(false);
    const [data, setData] = React.useState([]);
    const notificationAlertRef = useRef(null);

    const notify = (type, message) => {
        const options = {
            place: "tc",
            message: <div>{message}</div>,
            type: type,
            icon: "tim-icons icon-bell-55",
            autoDismiss: 5,
        };
        notificationAlertRef.current.notificationAlert(options);
    };

    useEffect(() => {
        const getUsers = async () => {
            try {
                const response = await authService.authenticatedFetch(rest.users, {
                    method: 'GET'
                });
                if (response.ok) {
                    const usersData = await response.json();
                    setUsers(usersData);

                    // Map users to table data format
                    const tableData = usersData.map((user, i) => {
                        return {
                            id: user._id,
                            Email: user.Email || user.Userid,
                            FirstName: user.Fname,
                            LastName: user.Lname,
                            Role: user.Role,
                            actions: (
                                <div className="actions-right">
                                    <OverlayTrigger placement="top"
                                                    overlay={<Tooltip id={"add_" + i}>Add New User</Tooltip>}>
                                        <span>
                                        <Button
                                            onClick={async () => {
                                                await history.push("/admin/adduser");
                                            }}
                                            color="info"
                                            size="sm"
                                            className={classNames("btn-icon btn-link like",)}
                                        >
                                            <i className="tim-icons icon-simple-add"/>
                                        </Button>{" "}
                                        </span>
                                    </OverlayTrigger>

                                    <OverlayTrigger placement="top" overlay={<Tooltip id={"edit_" + i}>Edit User</Tooltip>}>
                                        <span>
                                        <Button
                                            onClick={async () => {
                                                setSelectedUser(user);
                                                await history.push("/admin/edituser", { user });
                                            }}
                                            color="warning"
                                            size="sm"
                                            className={classNames("btn-icon btn-link like",)}
                                        >
                                            <i className="tim-icons icon-pencil"/>
                                        </Button>{" "}
                                        </span>
                                    </OverlayTrigger>

                                    <OverlayTrigger placement="top"
                                                    overlay={<Tooltip id={"delete_" + i}>Delete User</Tooltip>}>
                                        <span>
                                        <Button
                                            onClick={async () => {
                                                _deleteUserAlert(user._id, user.Email);
                                            }}
                                            color="danger"
                                            size="sm"
                                            className={classNames("btn-icon btn-link like",)}
                                        >
                                            <i className="tim-icons icon-simple-remove"/>
                                        </Button>{" "}
                                        </span>
                                    </OverlayTrigger>
                                </div>
                            )
                        };
                    });
                    setData(tableData);
                }
            } catch (error) {
                console.error('Error fetching users:', error);
            }
        };

        if (userState.status && userState.role === 'Admin') {
            getUsers();
        }
    }, [dataLoaded, userState.status, userState.role]);

    const _delete_user = async (id) => {
        try {
            let tempURL = rest.users + '/' + id;
            const response = await authService.authenticatedFetch(tempURL, {
                method: 'DELETE'
            });

            if (response.ok) {
                notify("success", "User has been deleted successfully!");
                // Refresh the users list
                setDataLoaded(!dataLoaded);
            } else {
                const errorData = await response.json();
                notify("danger", "Error deleting user: " + (errorData.message || "Unknown error"));
            }
        } catch (error) {
            console.error('Error deleting user:', error);
            notify("danger", "Error deleting user: " + error.message);
        }
    };

    const _deleteUserAlert = async (id, email) => {
        // Prevent admin from deleting their own account
        if (id === userState._id) {
            setalert(
                <SweetAlert
                    danger
                    confirmBtnText="OK"
                    confirmBtnBsStyle="info"
                    title="Cannot Delete Own Account"
                    style={{display: "block"}}
                    onConfirm={() => {
                        setalert(null);
                    }}
                >
                    You cannot delete your own account. Please ask another administrator to delete your account if needed.
                </SweetAlert>
            );
            return;
        }

        setalert(
            <SweetAlert
                warning
                showCancel
                confirmBtnText="Yes, delete it!"
                confirmBtnBsStyle="danger"
                cancelBtnBsStyle="default"
                title="Are you sure?"
                style={{display: "block"}}
                onConfirm={() => {
                    _delete_user(id);
                    setalert(null);
                }}
                onCancel={() =>  {
                    setalert(null);
                }}
            >
                Are you sure you want to delete user: {email}?
            </SweetAlert>
        );
    };

    if (userState.status && userState.role === 'Admin') {
        return (
            <>
                <div className="rna-container">
                    <NotificationAlert ref={notificationAlertRef} />
                </div>
                <div className="content">
                    <Col md={8} className="ml-auto mr-auto">
                        <h2 className="text-center">Manage Users</h2>
                        <p className="text-center">
                            Manage system users and their roles (Admin Only)
                        </p>
                    </Col>
                    <Row className="mt-5">
                        <Col xs={12} md={12}>
                            <Card>
                                <CardHeader>
                                    <Row>
                                        <Col md="6">
                                            <CardTitle tag="h4">System Users</CardTitle>
                                        </Col>
                                        <Col md="6" className="text-right">
                                            <Button
                                                color="info"
                                                onClick={() => history.push("/admin/adduser")}
                                                className="animation-on-hover"
                                            >
                                                <i className="tim-icons icon-simple-add"/> Add User
                                            </Button>
                                        </Col>
                                    </Row>
                                </CardHeader>
                                <CardBody>
                                    <ReactTable
                                        data={data}
                                        filterable
                                        columns={[
                                            {
                                                Header: "Email",
                                                accessor: "Email"
                                            },
                                            {
                                                Header: "First Name",
                                                accessor: "FirstName"
                                            },
                                            {
                                                Header: "Last Name",
                                                accessor: "LastName"
                                            },
                                            {
                                                Header: "Role",
                                                accessor: "Role"
                                            },
                                            {
                                                Header: "Actions",
                                                accessor: "actions",
                                                sortable: false,
                                                filterable: false
                                            }
                                        ]}
                                        defaultPageSize={10}
                                        showPaginationTop
                                        showPaginationBottom={false}
                                        className="-striped -highlight"
                                    />
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                    {alert}
                </div>
            </>
        );
    } else {
        history.push("/admin/dashboard");
        return(null);
    }
};

export default UsersTable;
